"use client";

import "./globals.css";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import {
  Rss,
  Send,
  BarChart3,
  Zap,
  Settings,
  Users,
  PenLine,
  LogOut,
  ChevronsUpDown,
  UserCheck,
  FlaskConical,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "@/components/ui/sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";

interface NavItem {
  name: string;
  href: string;
  icon: typeof BarChart3;
  permission?: string;
  mandateAccess?: boolean;
}

const mainNav: NavItem[] = [
  { name: "Dashboard", href: "/dashboard", icon: BarChart3, permission: "dashboard.view" },
  { name: "My story", href: "/submit", icon: PenLine },
];

const adminNav: NavItem[] = [
  { name: "Sources", href: "/sources", icon: Rss, permission: "sources.manage" },
  { name: "Canale de distribuție", href: "/destinations", icon: Send, permission: "destinations.manage", mandateAccess: true },
  { name: "Utilizatori", href: "/users", icon: Users, permission: "users.manage" },
  { name: "Playground", href: "/playground", icon: FlaskConical, permission: "playground.use" },
  { name: "Setări", href: "/settings", icon: Settings, permission: "settings.manage" },
];

function filterNav(items: NavItem[], can: (p: string) => boolean, hasMandates: boolean) {
  return items.filter((item) => {
    if (!item.permission) return true;
    if (can(item.permission)) return true;
    if (item.mandateAccess && hasMandates) return true;
    return false;
  });
}

function AppSidebar() {
  const pathname = usePathname();
  const { user, logout, hasPermission } = useAuth();

  const hasMandates = !!(user?.assigned_destination_ids?.length);
  const visibleMain = filterNav(mainNav, hasPermission, hasMandates);
  const visibleAdmin = filterNav(adminNav, hasPermission, hasMandates);

  const isActive = (href: string) =>
    href === "/dashboard" ? pathname === "/dashboard" : pathname.startsWith(href);

  const roleLabels: Record<string, string> = {
    admin: "Administrator",
    editor: "Editor",
    user: "Utilizator",
  };

  return (
    <Sidebar>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href="/dashboard">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Zap className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Alephia</span>
                  <span className="truncate text-xs text-muted-foreground">News Aggregation</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Principal</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleMain.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton asChild isActive={isActive(item.href)} tooltip={item.name}>
                    <Link href={item.href}>
                      <item.icon />
                      <span>{item.name}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {visibleAdmin.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Administrare</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleAdmin.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={isActive(item.href)} tooltip={item.name}>
                      <Link href={item.href}>
                        <item.icon />
                        <span>{item.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-primary/10 text-primary">
                      {user?.username?.charAt(0).toUpperCase() ?? "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user?.username}</span>
                    <span className="truncate text-xs">{roleLabels[user?.role ?? ""] ?? user?.role}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-primary/10 text-primary">
                        {user?.username?.charAt(0).toUpperCase() ?? "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{user?.username}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {roleLabels[user?.role ?? ""] ?? user?.role}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Deconectare
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

function ImpersonationBanner() {
  const { user, isImpersonating, stopImpersonation } = useAuth();
  const [stopping, setStopping] = useState(false);

  if (!isImpersonating || !user) return null;

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopImpersonation();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Eroare");
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="sticky top-0 z-[60] flex items-center justify-center gap-3 bg-warning px-4 py-2 text-sm font-medium text-warning-foreground shadow-md">
      <UserCheck className="h-4 w-4" />
      <span>
        Impersonezi <strong>{user.username}</strong> ({user.role})
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleStop}
        disabled={stopping}
        className="ml-2 bg-white/20 text-white backdrop-blur hover:bg-white/30"
      >
        {stopping ? "Se revine..." : "Revino la contul tău"}
      </Button>
    </div>
  );
}

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user, loading } = useAuth();

  if (pathname === "/login") {
    return <>{children}</>;
  }

  // Ecranele kiosk din redacție (token în URL, fără login, fără shell).
  if (pathname.startsWith("/matrix-") || pathname.startsWith("/tv-")) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="text-sm text-muted-foreground">Se încarcă...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    return null;
  }

  const bareShellRoutes = [
    "/dashboard",
    "/sources",
    "/destinations",
    "/submit",
    "/settings",
    "/monitor",
    "/monitor-social",
    "/head",
    "/head-social",
    "/top-social",
    "/alephia",
    "/sumar",
    "/sumar20",
    "/newsroom",
    "/workspace",
    "/users",
    "/roles",
    "/playground",
    "/profile",
  ];
  if (bareShellRoutes.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return <>{children}</>;
  }

  return (
    <SidebarProvider>
      <ImpersonationBanner />
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs text-muted-foreground">Online</span>
          </div>
        </header>
        <main className="flex-1 p-6">{children}</main>
      </SidebarInset>
      <Toaster />
    </SidebarProvider>
  );
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <html lang="ro">
      {/* suppressHydrationWarning: extensiile de browser (AdBlock, Grammarly)
          injectează atribute în body înainte de hidratare → avertisment fals. */}
      <body className="min-h-screen bg-background font-sans antialiased" suppressHydrationWarning>
        <AuthProvider>
          <AuthenticatedLayout>{children}</AuthenticatedLayout>
        </AuthProvider>
      </body>
    </html>
  );
}
